import React, { useState, useEffect } from 'react';
import { Clock, Play, Trash2, Calendar, MoreHorizontal, Send } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';

export default function Queue({ onNavigate }) {
  const [videos, setVideos] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('videos')
      .select('*, tiktok_accounts(username)')
      .in('status', ['scheduled', 'publishing', 'draft'])
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setVideos((data ?? []).map(({ tiktok_accounts, ...v }) => ({
          ...v, account_name: tiktok_accounts?.username ?? null,
        })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    try {
      await supabase.from('videos').delete().eq('id', id);
      setVideos(prev => prev.filter(v => v.id !== id));
    } catch {}
    setOpenMenu(null);
  };

  const handlePublishNow = async (id) => {
    setOpenMenu(null);
    try {
      await supabase.functions.invoke('videos-publish', { body: { video_id: id } });
      setVideos(prev => prev.filter(v => v.id !== id));
    } catch {}
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Scheduled Queue</h1>
          <p style={styles.subtitle}>Videos waiting to be published automatically</p>
        </div>
        <button style={styles.addBtn} onClick={() => onNavigate('upload')}>
          + New Video
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14, textAlign: 'center', padding: '40px 0' }}>Loading...</p>
      ) : (
        <div style={styles.list}>
          {videos.map(video => (
            <div key={video.id} style={styles.card}>
              <div style={styles.cardThumb}>
                <Play size={20} color="var(--text-muted)" />
              </div>

              <div style={styles.cardBody}>
                <div style={styles.cardTop}>
                  <div style={styles.cardMeta}>
                    <span style={styles.statusBadge}>
                      <Clock size={11} /> Scheduled
                    </span>
                    <span style={styles.account}>
                      {video.account_name ? `@${video.account_name}` : '—'}
                    </span>
                  </div>
                  <div style={styles.menuWrapper}>
                    <button
                      style={styles.menuBtn}
                      onClick={() => setOpenMenu(openMenu === video.id ? null : video.id)}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <MoreHorizontal size={16} color="var(--text-secondary)" />
                    </button>
                    {openMenu === video.id && (
                      <div style={styles.menu}>
                        <button style={styles.menuItem} onClick={() => handlePublishNow(video.id)}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <Send size={14} /> Publish Now
                        </button>
                        <button style={{ ...styles.menuItem, color: 'var(--pink)' }} onClick={() => handleDelete(video.id)}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--pink-light)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <h3 style={styles.cardTitle}>{video.title}</h3>
                <p style={styles.cardCaption}>{video.caption}</p>

                <div style={styles.cardFooter}>
                  <div style={styles.cardStats}>
                    {video.file_size && (
                      <span style={styles.stat}>💾 {(video.file_size / 1024 / 1024).toFixed(1)} MB</span>
                    )}
                  </div>
                  <div style={styles.cardTime}>
                    <Calendar size={12} />
                    {video.scheduled_at
                      ? format(new Date(video.scheduled_at), "MMM dd, HH:mm")
                      : 'Not scheduled'}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {videos.length === 0 && (
            <div style={styles.empty}>
              <p style={styles.emptyText}>No scheduled videos</p>
              <button style={styles.emptyBtn} onClick={() => onNavigate('upload')}>Upload Video</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { flex: 1, padding: '32px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 },
  subtitle: { fontSize: 14, color: 'var(--text-secondary)' },
  addBtn: {
    padding: '10px 20px', background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: {
    display: 'flex', gap: 16, background: 'var(--bg-card)',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px',
  },
  cardThumb: {
    width: 88, height: 88, borderRadius: 10, background: 'var(--bg-secondary)',
    border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  cardBody: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardMeta: { display: 'flex', alignItems: 'center', gap: 8 },
  statusBadge: {
    display: 'flex', alignItems: 'center', gap: 4,
    fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20,
    color: 'var(--accent)', background: 'var(--accent-light)',
  },
  account: { fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 },
  menuWrapper: { position: 'relative' },
  menuBtn: { padding: '4px 8px', background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer' },
  menu: {
    position: 'absolute', right: 0, top: '100%', marginTop: 4,
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 10, zIndex: 100, overflow: 'hidden',
    boxShadow: 'var(--shadow)', minWidth: 140,
  },
  menuItem: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
    background: 'transparent', border: 'none', cursor: 'pointer', width: '100%',
    fontSize: 13, color: 'var(--text-secondary)',
  },
  cardTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' },
  cardCaption: { fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 },
  cardStats: { display: 'flex', gap: 12 },
  stat: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)' },
  cardTime: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 },
  empty: { textAlign: 'center', padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  emptyText: { color: 'var(--text-muted)', fontSize: 15 },
  emptyBtn: {
    padding: '10px 24px', background: 'var(--accent)', color: '#fff',
    border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
};
